import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Account, Tenant, VoucherSettings, VoucherResetPeriod } from 'src/models/account.model';
import { User } from 'src/models/user.model';
import { AuthService } from 'src/services/auth.service';
import { TenantService } from 'src/services/tenant.service';
import { AccountService } from 'src/services/account.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-update-tenant',
  templateUrl: './update-tenant.component.html'
})
export class UpdateTenantComponent implements OnInit {

  tenantForm: FormGroup;
  account: Account;
  userAuth: User;
  tenantId: string;
  tenant: Tenant;
  showPin: boolean = false;
  existingUsernames: string[] = [];

  freeHoursOptions = [1, 2, 3, 4, 5, 6, 7, 8];
  expirationOptions = [
    { value: 12, label: '12 hours' },
    { value: 24, label: '24 hours' },
    { value: 48, label: '48 hours' },
    { value: 72, label: '72 hours (3 days)' },
    { value: 168, label: '168 hours (7 days)' }
  ];
  resetPeriodOptions: { value: VoucherResetPeriod; label: string }[] = [
    { value: 'none', label: 'No Reset (Lifetime Limit)' },
    { value: 'daily', label: 'Daily (Resets every day)' }
  ];

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    public auth: AuthService,
    private tenantService: TenantService,
    private accountService: AccountService
  ) {
    this.account = JSON.parse(localStorage.getItem('account'));
    this.initializeForm();
  }

  ngOnInit() {
    this.auth.user$.subscribe(user => {
      this.userAuth = user;
    });

    this.tenantId = this.route.snapshot.paramMap.get('id');
    this.loadTenant();
    this.initializeExistingUsernames();
  }

  initializeForm() {
    this.tenantForm = this.formBuilder.group({
      tenant_id: [''],
      store_name: ['', Validators.required],
      tenant_name: ['', Validators.required],
      phone_number: [''],
      email: ['', Validators.email],
      username: [''],
      password_pin: [''],
      free_parking_hours: [1, Validators.required],
      generation_limit: [null],
      unlimited_vouchers: [true],
      expiration_hours: [24, Validators.required],
      reset_period: ['none'],
      is_active: [true]
    });
  }

  initializeExistingUsernames() {
    if (this.account?.settings?.tenants) {
      this.existingUsernames = this.account.settings.tenants
        .filter(t => t.tenant_id !== this.tenantId)
        .map(t => t.username);
    }
  }

  loadTenant() {
    this.tenant = this.tenantService.getTenantById(this.account, this.tenantId);

    if (!this.tenant) {
      Swal.fire({
        icon: 'error',
        title: 'Tenant Not Found',
        text: 'The requested tenant could not be found.'
      }).then(() => {
        this.router.navigate([this.account.account_key + '/user/tenants']);
      });
      return;
    }

    const hasLimit = this.tenant.voucher_settings?.generation_limit !== null &&
                     this.tenant.voucher_settings?.generation_limit !== undefined;

    this.tenantForm.patchValue({
      tenant_id: this.tenant.tenant_id,
      store_name: this.tenant.store_name,
      tenant_name: this.tenant.tenant_name,
      phone_number: this.tenant.phone_number,
      email: this.tenant.email,
      username: this.tenant.username,
      password_pin: this.tenant.password_pin,
      free_parking_hours: this.tenant.voucher_settings?.free_parking_hours || 1,
      generation_limit: this.tenant.voucher_settings?.generation_limit,
      unlimited_vouchers: !hasLimit,
      expiration_hours: this.tenant.voucher_settings?.expiration_hours || 24,
      reset_period: this.tenant.voucher_settings?.reset_period || 'none',
      is_active: this.tenant.is_active
    });
  }

  get f() {
    return this.tenantForm.controls;
  }

  toggleShowPin() {
    this.showPin = !this.showPin;
  }

  onUnlimitedChange() {
    const unlimited = this.tenantForm.get('unlimited_vouchers').value;
    if (unlimited) {
      this.tenantForm.patchValue({ generation_limit: null });
    }
  }

  async regeneratePin() {
    const result = await Swal.fire({
      title: 'Regenerate PIN',
      text: 'This will generate a new PIN for this tenant. The old PIN will no longer work.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, regenerate',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      const newPin = this.tenantService.generatePin();
      this.tenantForm.patchValue({ password_pin: newPin });

      Swal.fire({
        title: 'New PIN Generated!',
        html: `<p>New PIN: <strong>${newPin}</strong></p>`,
        icon: 'success'
      });
    }
  }

  async resetVoucherCount() {
    const result = await Swal.fire({
      title: 'Reset Voucher Count',
      text: 'This will reset the voucher generation count to 0. This cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, reset',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      await Swal.fire({
        title: 'Resetting...',
        allowEscapeKey: false,
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: async () => {
          Swal.showLoading();

          try {
            await this.tenantService.resetVoucherCount(this.account, this.tenantId);

            // Refresh account
            const updatedAccount = await this.accountService.getAccountByKey(this.account.account_key);
            localStorage.setItem('account', JSON.stringify(updatedAccount));
            this.account = updatedAccount;
            this.tenant = this.tenantService.getTenantById(this.account, this.tenantId);

            Swal.fire({
              title: 'Success!',
              text: 'Voucher count has been reset to 0.',
              icon: 'success',
              timer: 2000,
              showConfirmButton: false
            });
          } catch (error) {
            Swal.fire('Error', 'Failed to reset voucher count.', 'error');
          }
        }
      });
    }
  }

  async save() {
    if (this.tenantForm.invalid) {
      await Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please fill in all required fields.'
      });
      return;
    }

    await Swal.fire({
      title: 'Updating tenant...',
      allowEscapeKey: false,
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: async () => {
        Swal.showLoading();

        try {
          const formData = this.tenantForm.value;

          const voucherSettings: VoucherSettings = {
            free_parking_hours: parseInt(formData.free_parking_hours),
            generation_limit: formData.unlimited_vouchers ? null : parseInt(formData.generation_limit),
            vouchers_generated: this.tenant.voucher_settings?.vouchers_generated || 0,
            expiration_hours: parseInt(formData.expiration_hours),
            reset_period: formData.unlimited_vouchers ? 'none' : formData.reset_period
          };

          const updatedTenant: Tenant = {
            ...this.tenant,
            store_name: formData.store_name,
            tenant_name: formData.tenant_name,
            phone_number: formData.phone_number,
            email: formData.email,
            password_pin: formData.password_pin,
            voucher_settings: voucherSettings,
            is_active: formData.is_active
          };

          await this.tenantService.updateTenant(this.account, updatedTenant);

          // Refresh account in localStorage
          const updatedAccount = await this.accountService.getAccountByKey(this.account.account_key);
          localStorage.setItem('account', JSON.stringify(updatedAccount));

          Swal.fire({
            title: 'Success!',
            text: 'Tenant updated successfully.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
          }).then(() => {
            this.router.navigate([this.account.account_key + '/user/tenants']);
          });

        } catch (error) {
          console.error('Error updating tenant:', error);
          Swal.fire({
            title: 'Error',
            text: 'Failed to update tenant. Please try again.',
            icon: 'error'
          });
        }
      }
    });
  }

  cancel() {
    this.router.navigate([this.account.account_key + '/user/tenants']);
  }

  getVouchersGenerated(): number {
    return this.tenant?.voucher_settings?.vouchers_generated || 0;
  }
}
